import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Gift, Send, TrendingUp, Upload } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { PATHS, useLang, useT } from "../../lib/i18n";
import { track } from "../../lib/tracking";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { BookieList } from "../../pages/VipPage";
import { FreeVipHead, FreeVipWhy } from "./FreeVipPitch";

export async function connectTelegram(refresh) {
  const { data } = await api.post("/me/telegram-link");
  window.open(data.url, "_blank", "noopener");
  let n = 0;
  const iv = setInterval(async () => {
    n += 1;
    const u = await refresh();
    if (u?.telegram_chat_id) { track("telegram_connect"); clearInterval(iv); toast.success("Telegram ✓"); }
    if (n > 24) clearInterval(iv);
  }, 5000);
}

export function WelcomeModal({ user, refresh }) {
  const t = useT();
  const [sp, setSp] = useSearchParams();
  const open = sp.get("welcome") === "1";
  const close = () => { sp.delete("welcome"); setSp(sp, { replace: true }); };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md" data-testid="welcome-modal">
        <DialogHeader><DialogTitle className="font-display text-3xl">{t("Willkommen bei TennisTipps!", "Welcome to TennisTipps!")}</DialogTitle></DialogHeader>
        <ul className="space-y-2 text-sm text-muted-foreground">
          {[t("Alle Gratis-Tipps mit Begründung", "All free tips with reasoning"), t("Täglich 1 exklusiver Member-Tipp", "1 exclusive member tip every day"), t("1 Jahr VIP gratis über Partner-Buchmacher", "1 year free VIP via partner bookmakers")].map((x) => <li key={x} className="flex gap-2"><Check size={16} className="text-[var(--ball)]" />{x}</li>)}
        </ul>
        <p className="mt-2 text-sm">{t("Verbinde Telegram, um neue Tipps sofort zu erhalten:", "Connect Telegram to get new tips instantly:")}</p>
        {user.telegram_chat_id ? <p className="text-sm text-[var(--win)]">✓ Telegram</p>
          : <Button className="rounded-full" onClick={() => connectTelegram(refresh).catch((e) => toast.error(errMsg(e)))} data-testid="welcome-telegram-btn"><Send size={16} className="mr-2" />{t("Telegram verbinden", "Connect Telegram")}</Button>}
        <Button variant="ghost" className="rounded-full" onClick={close} data-testid="welcome-close-btn">{t("Zu meinen Tipps", "Go to my tips")}</Button>
      </DialogContent>
    </Dialog>
  );
}

export function PerfBanner({ perf }) {
  const t = useT();
  const lang = useLang();
  if (!perf) return null;
  const items = [perf.yesterday > 0 && [t("VIP gestern", "VIP yesterday"), perf.yesterday], perf.week > 0 && [t("Diese Woche verpasst", "Missed this week"), perf.week]].filter(Boolean);
  if (!items.length) return null;
  return (
    <Link to={PATHS[lang].vip} className="mt-6 flex flex-wrap items-center gap-4 rounded-xl border border-[var(--win)]/40 bg-[var(--win)]/10 p-4" data-testid="vip-perf-banner" onClick={() => track("vip_teaser_click", { from: "perf_banner" })}>
      <TrendingUp className="text-[var(--win)]" />
      {items.map(([l, v]) => <span key={l} className="text-sm">{l}: <b className="font-num text-lg text-[var(--win)]">+{v} Units</b></span>)}
      <span className="ml-auto text-sm font-semibold text-[var(--ball)]">{t("VIP freischalten", "Unlock VIP")} →</span>
    </Link>
  );
}

export function Checklist({ c, onTab }) {
  const t = useT();
  if (!c) return null;
  const items = [["email", t("E-Mail bestätigt", "Email confirmed")], ["telegram", t("Telegram verbunden", "Telegram connected"), "settings"], ["bookie", t("Bookie-VIP beantragt", "Bookie VIP requested"), "free"], ["vip", t("VIP aktiv", "VIP active"), "sub"]];
  const done = items.filter(([k]) => c[k]).length;
  return (
    <div className="surface mt-6 p-5" data-testid="onboarding-checklist">
      <div className="flex items-center justify-between text-sm"><span className="font-semibold">{t("Dein Start", "Your setup")}</span><span className="font-num text-muted-foreground">{done}/{items.length}</span></div>
      <div className="mt-2 h-1.5 rounded-full bg-border"><div className="h-1.5 rounded-full bg-[var(--ball)] transition-[width] duration-500" style={{ width: `${(done / items.length) * 100}%` }} /></div>
      <div className="mt-4 grid gap-2 sm:grid-cols-4">
        {items.map(([k, l, tab]) => (
          <button key={k} disabled={c[k] || !tab} onClick={() => tab && onTab(tab)} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${c[k] ? "border-[var(--win)]/40 text-[var(--win)]" : "border-border hover:border-[var(--ball)]"}`} data-testid={`checklist-${k}`}>
            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${c[k] ? "bg-[var(--win)] text-[#07090c]" : "border border-border"}`}>{c[k] && <Check size={12} />}</span>{l}
          </button>
        ))}
      </div>
    </div>
  );
}

export function BookieHero({ d, user, onUpload }) {
  const t = useT();
  const lang = useLang();
  const [country, setCountry] = useState(user.country || "");
  useEffect(() => { setCountry(user.country || ""); }, [user.country]);
  return (
    <section className="surface relative mt-6 overflow-hidden border-[var(--ball)]/50 p-6 shadow-[0_0_80px_-30px_rgba(215,255,58,.5)]" data-testid="bookie-hero">
      <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[var(--ball)]/10 blur-3xl" />
      <div className="relative grid gap-6 lg:grid-cols-[1fr_1.2fr]">
        <div>
          {d.has_bookie ? <FreeVipHead size="md" /> : <>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ball)]"><Gift size={14} />{t("Dein Angebot", "Your offer")}</p>
          <h2 className="mt-2 font-display text-4xl font-extrabold leading-none">{t("50% auf den ersten Monat", "50% off your first month")}</h2>
          <p className="mt-3 text-sm text-muted-foreground">{t("Für dein Land gibt es keinen Partner-Buchmacher – dafür 50% auf den ersten Monat oder 30% aufs Jahrespaket.", "No partner bookmaker in your country – instead 50% off the first month or 30% off the annual package.")}</p></>}
          {d.has_bookie && <div className="mt-4"><FreeVipWhy /></div>}
          {d.has_bookie ? <Button variant="secondary" className="mt-5 rounded-full" onClick={onUpload} data-testid="bookie-hero-upload-btn"><Upload size={16} className="mr-2" />{t("Screenshot hochladen", "Upload screenshot")}</Button>
            : <Link to={`${PATHS[lang].vip}?offer=nobookie`} onClick={() => track("vip_teaser_click", { from: "nobookie_offer" })}><Button className="mt-5 rounded-full" data-testid="bookie-hero-offer-btn">{t("Rabatt sichern", "Get discount")}</Button></Link>}
        </div>
        {d.has_bookie && <BookieList country={country} setCountry={setCountry} compact />}
      </div>
    </section>
  );
}

export function Packages({ d }) {
  const t = useT();
  const lang = useLang();
  const p = d.products || {};
  const list = [["monthly", t("VIP Monat", "VIP Month"), t("/Monat, monatlich kündbar", "/month, cancel monthly")], ["annual", t("Jahrespaket", "Annual package"), t(" inkl. Grand Slams", " incl. Grand Slams")], ...(d.gs_on_sale ? [["grand_slam", "Grand Slam Pass", ""]] : [])];
  const offer = d.offer50_until ? "&offer=n50" : "";
  return (
    <section className="mt-8" data-testid="member-packages">
      <h2 className="font-display text-2xl font-bold">{t("Oder direkt VIP werden", "Or go VIP right away")}</h2>
      {d.offer50_until && <p className="mt-2 rounded-lg border border-[var(--ball)]/40 bg-[var(--ball)]/5 p-3 text-sm" data-testid="offer50-note">{t("Dein 50%-Gutschein für den ersten Monat ist aktiv bis", "Your 50% first-month voucher is active until")} {new Date(d.offer50_until).toLocaleString(lang === "de" ? "de-DE" : "en-GB", { timeZone: "Europe/Berlin", dateStyle: "short", timeStyle: "short" })}</p>}
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        {list.map(([k, n, note]) => p[k] && (
          <Link key={k} to={`${PATHS[lang].vip}?product=${k}${k === "monthly" ? offer : ""}`} className="surface surface-hover flex flex-col p-5" data-testid={`member-package-${k}`} onClick={() => track("vip_teaser_click", { from: "member_packages", product: k })}>
            <span className="font-display text-xl font-bold">{n}</span>
            <span className="mt-2 font-num text-3xl font-bold">{p[k].price}€<span className="text-xs font-normal text-muted-foreground">{note}</span></span>
            <span className="mt-4 text-sm font-semibold text-[var(--ball)]">{t("Auswählen", "Choose")} →</span>
          </Link>
        ))}
      </div>
    </section>
  );
}
